import React, { useState, useEffect, useRef } from 'react';
import { LazyImage } from '../common/LazyImage';
import { LazyVideo } from '../common/LazyVideo';
import { Button } from '../common/Button';
import { 
  Chrome, 
  Zap, 
  Target, 
  TrendingUp, 
  Download,
  ExternalLink,
  CheckCircle,
  Star,
  Users,
  Clock,
  Shield,
  Hand,
  ThumbsUp,
  Linkedin,
  CreditCard,
  Currency,
  DollarSign,
} from 'lucide-react';
import { trackSectionView, trackButtonClick } from '../../firebaseConfig';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';
import { getOptimizedImageUrl, getImageSources } from '../../lib/optimizedAssets';

export function ExtensionShowcase() {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDark, setIsDark] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { trackButtonClick: trackAnalyticsButton, trackConversion } = useAnalyticsTracking();

  // Image slides for auto-changing
  const imageSlides = [
    {
      dark: 'mocktrail-extn-pop-darkui',
      light: 'mocktrail-extn-pop-lightui',
      mobileDark: 'mocktrail-extn-pop-mobile-darkui',
      mobileLight: 'mocktrail-extn-pop-mobile-lightui',
      alt: 'MockTrail Extension Popup'
    },
    {
      dark: 'mocktrail-extn-res-darkui',
      light: 'mocktrail-extn-res-lightui',
      mobileDark: 'mocktrail-extn-res-darkui',
      mobileLight: 'mocktrail-extn-res-lightui',
      alt: 'MockTrail Resume Analysis'
    }
  ];

  // Detect mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect theme change and initialize
  useEffect(() => {
    // Check for dark mode
    const checkDarkMode = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };

    // Initial check
    checkDarkMode();

    // Set up listeners for changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.attributeName === 'class' &&
          mutation.target === document.documentElement
        ) {
          checkDarkMode();
        }
      });
    });

    // Start observing document for class changes
    observer.observe(document.documentElement, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Auto-change images
  useEffect(() => {
    if (!isVisible || imageSlides.length <= 1) return;
    
    const interval = 4000; // 4 seconds per image
    const progressInterval = 50; // Update progress every 50ms
    
    // Main timer for changing images
    const imageTimer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % imageSlides.length);
        setProgress(0);
        setIsTransitioning(false);
      }, 300);
    }, interval);
    
    // Progress timer for smooth progress bar
    const progressTimer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (progressInterval / interval) * 100;
        return newProgress > 100 ? 100 : newProgress;
      });
    }, progressInterval);
    
    return () => {
      clearInterval(imageTimer);
      clearInterval(progressTimer);
    };
  }, [isVisible, imageSlides.length]);

  // Track section view when component mounts and handle animations
  useEffect(() => {
    trackSectionView('Extension Showcase Section');
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Staggered animation timeline with faster timing
          setTimeout(() => setAnimationPhase(1), 50);  // Title - reduced from 100ms
          setTimeout(() => setAnimationPhase(2), 150); // Description - reduced from 300ms
          setTimeout(() => setAnimationPhase(3), 250); // Features - reduced from 500ms
          setTimeout(() => setAnimationPhase(4), 350); // Demo - reduced from 700ms
          setTimeout(() => setAnimationPhase(5), 450); // CTA - reduced from 900ms
          
          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Trigger earlier when only 10% of section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: TrendingUp,
      title: "Get Job-Resume Match Score"
    },
    {
      icon: Target,
      title: "Practice Interviews For Any Job"
    },
    {
      icon: DollarSign,
      title: "Free To Use With No Limits"
    }
  ];

  const handleDownloadExtension = () => {
    trackAnalyticsButton('Download Extension', 'Extension Showcase', {
      button_type: 'primary_cta',
      button_position: 'extension_showcase',
      conversion_intent: 'extension_download',
      utm_source: 'extension_showcase',
      utm_medium: 'cta_button',
      utm_campaign: 'extension_download'
    });
    
    trackConversion('extension_download', {
      source: 'extension_showcase',
      button_name: 'Download Extension'
    });
    
    trackButtonClick('Download Extension', 'Extension Showcase');
    
    // Open Chrome Web Store in new tab
    window.open('https://chromewebstore.google.com/detail/mocktrail-resume-score-in/ljopoadaipkmgclmpljgachiciakomgo', '_blank');
  };

  return (
    <section 
      ref={sectionRef}
      id="extension-showcase" 
      className="py-32 relative overflow-hidden bg-gradient-to-b from-surface-light/30 to-transparent dark:from-surface-dark/30 dark:to-transparent"
    >
      {/* Background Effects with animations */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5 opacity-0 transition-opacity duration-700" 
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(37,99,235,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)] parallax-element opacity-0 transition-opacity duration-700 delay-150" 
        data-speed="0.05"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(16,185,129,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_70%_50%,rgba(52,211,153,0.05),transparent_50%)] parallax-element opacity-0 transition-opacity duration-700 delay-200" 
        data-speed="0.07"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      
      {/* Floating particles */}
      <div 
        className="absolute left-[20%] top-[15%] w-5 h-5 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float parallax-element opacity-0 transition-opacity duration-700 delay-250" 
        data-speed="0.13"
        style={{ opacity: isVisible ? 0.6 : 0, animationDelay: '0.5s', animationDuration: '6s' }}
      />
      <div 
        className="absolute right-[25%] top-[30%] w-4 h-4 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float parallax-element opacity-0 transition-opacity duration-700 delay-300" 
        data-speed="0.10"
        style={{ opacity: isVisible ? 0.7 : 0, animationDelay: '0.3s', animationDuration: '5s' }}
      />
      
      {/* Diagonal accent lines */}
      <div 
        className="absolute -left-32 top-52 w-[600px] h-[1px] bg-gradient-to-r from-transparent via-primary-light/15 to-transparent rotate-[25deg] parallax-element opacity-0 transition-opacity duration-700 delay-200" 
        data-speed="0.08"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute -right-32 top-96 w-[600px] h-[1px] bg-gradient-to-r from-transparent via-secondary-light/15 to-transparent -rotate-[25deg] parallax-element opacity-0 transition-opacity duration-700 delay-300" 
        data-speed="0.09"
        style={{ opacity: isVisible ? 1 : 0 }}
      />

      <div className="container mx-auto relative px-4 md:px-0">
        {/* Header */}
        <div className="text-center mb-20">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark text-sm font-medium mb-6 transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <Chrome className="w-4 h-4" />
            Browser Extension
          </div>
          
          <h2 
            className={`text-3xl md:text-4xl font-bold mb-6 text-text-primary-light dark:text-text-primary-dark transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Job Hunt Smarter with Our Chrome Extension
          </h2>
          
          <p 
            className={`text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Get instant <span className='text-text-primary-light dark:text-text-primary-dark font-medium'>Job-Resume match score</span>, <span className='text-text-primary-light dark:text-text-primary-dark font-medium'>One-Click mock interview</span> directly on LinkedIn and Naukri. 
            Transform your job search with AI-powered insights at every step.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-5 gap-16 items-center max-w-6xl mx-auto">
          {/* Images on Left - 60% width (3/5 columns) */}
          <div 
            className={`lg:col-span-3 transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            {/* Image container with ExperienceCard-like styling */}
            <div className="relative h-[500px] rounded-2xl p-1.5 bg-gradient-to-br from-primary-light/10 via-secondary-light/10 to-primary-light/10 dark:from-primary-dark/10 dark:via-secondary-dark/10 dark:to-primary-dark/10 shadow-lg">
              <div className="group relative h-full overflow-hidden rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md">
                {/* Auto-changing image */}
                <div className="absolute inset-0">
                  <picture>
                    <source 
                      srcSet={getOptimizedImageUrl(
                        isMobile 
                          ? (isDark ? imageSlides[currentImageIndex].mobileDark : imageSlides[currentImageIndex].mobileLight)
                          : (isDark ? imageSlides[currentImageIndex].dark : imageSlides[currentImageIndex].light), 
                        'webp'
                      )} 
                      type="image/webp" 
                    />
                    <img 
                      src={getOptimizedImageUrl(
                        isMobile 
                          ? (isDark ? imageSlides[currentImageIndex].mobileDark : imageSlides[currentImageIndex].mobileLight)
                          : (isDark ? imageSlides[currentImageIndex].dark : imageSlides[currentImageIndex].light), 
                        'webp'
                      )}
                      alt={imageSlides[currentImageIndex].alt}
                      className={`w-full h-full object-cover ${isTransitioning ? 'opacity-50 scale-105' : 'opacity-100 scale-100'} transform transition-all duration-500 group-hover:scale-110 transition-transform duration-700 ease-out`}
                      loading="lazy"
                    />
                  </picture>
                  
                  {/* Inner shadow overlay - same as HowItWorksCard and ExperienceCard */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-black/10 to-transparent" />
                </div>
                
                {/* Progress indicators */}
                <div className="absolute bottom-4 left-4 right-4 z-20">
                  <div className="flex gap-2 justify-start max-w-[40%]">
                    {imageSlides.map((_, index) => (
                      <div 
                        key={index} 
                        className="relative h-1 rounded-full bg-white/20 overflow-hidden"
                        style={{ width: index === currentImageIndex ? '30%' : '12%' }}
                      >
                        {index === currentImageIndex && (
                          <div 
                            className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-full"
                            style={{ width: `${progress}%`, transition: 'width 50ms linear' }}
                          />
                        )}
                        {index < currentImageIndex && (
                          <div className="absolute inset-0 bg-white/50 rounded-full" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features on Right - 40% width (2/5 columns) */}
          <div 
            className={`lg:col-span-2 space-y-6 transform transition-all duration-600 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 4 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            {/* Feature cards */}
            {features.map((feature, index) => {
              const Icon = feature.icon;
              
              return (
                <div
                  key={index}
                  className="group relative rounded-2xl transition-transform duration-300 ease-out hover:scale-[1.02]"
                >
                  {/* Animated border gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl animate-gradient bg-[length:200%_auto]" />
                  
                  {/* Glass container */}
                  <div className="relative p-4 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-gradient-to-br from-primary-light/10 to-secondary-light/10 dark:from-primary-dark/10 dark:to-secondary-dark/10 group-hover:scale-105 transition-transform duration-300">
                        <Icon className="w-5 h-5 text-primary-light dark:text-primary-dark" />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
                          {feature.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border-light dark:border-border-dark">
              <div className="text-center">
                <div className="text-xl font-bold text-primary-light dark:text-primary-dark mb-1">1K+</div>
                <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Jobs Analyzed</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-secondary-light dark:text-secondary-dark mb-1">4.8★</div>
                <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">User Rating</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-accent-light dark:text-accent-dark mb-1">500+</div>
                <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Interviews Practiced</div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="pt-4">
              <Button
                onClick={handleDownloadExtension}
                variant="primary"
                size="lg"
                leftIcon={
                  <svg 
                    className="w-5 h-5 transition-transform duration-300 group-hover:scale-110 -mt-0.5" 
                    xmlns="http://www.w3.org/2000/svg" 
                    xmlnsXlink="http://www.w3.org/1999/xlink" 
                    viewBox="0 0 48 48" 
                    height="48" 
                    width="48"
                  >
                    <defs>
                      <linearGradient id="a" x1="3.2173" y1="15" x2="44.7812" y2="15" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#d93025" />
                        <stop offset="1" stopColor="#ea4335" />
                      </linearGradient>
                      <linearGradient id="b" x1="20.7219" y1="47.6791" x2="41.5039" y2="11.6837" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#fcc934" />
                        <stop offset="1" stopColor="#fbbc04" />
                      </linearGradient>
                      <linearGradient id="c" x1="26.5981" y1="46.5015" x2="5.8161" y2="10.506" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#1e8e3e" />
                        <stop offset="1" stopColor="#34a853" />
                      </linearGradient>
                    </defs>
                    <circle cx="24" cy="23.9947" r="12" style={{fill:"#fff"}} />
                    <path d="M3.2154,36A24,24,0,1,0,12,3.2154,24,24,0,0,0,3.2154,36ZM34.3923,18A12,12,0,1,1,18,13.6077,12,12,0,0,1,34.3923,18Z" style={{fill:"none"}} />
                    <path d="M24,12H44.7812a23.9939,23.9939,0,0,0-41.5639.0029L13.6079,30l.0093-.0024A11.9852,11.9852,0,0,1,24,12Z" style={{fill:"url(#a)"}} />
                    <circle cx="24" cy="24" r="9.5" style={{fill:"#1a73e8"}} />
                    <path d="M34.3913,30.0029,24.0007,48A23.994,23.994,0,0,0,44.78,12.0031H23.9989l-.0025.0093A11.985,11.985,0,0,1,34.3913,30.0029Z" style={{fill:"url(#b)"}} />
                    <path d="M13.6086,30.0031,3.218,12.006A23.994,23.994,0,0,0,24.0025,48L34.3931,30.0029l-.0067-.0068a11.9852,11.9852,0,0,1-20.7778.007Z" style={{fill:"url(#c)"}} />
                  </svg>
                }
                analyticsName="Download Extension"
                analyticsLocation="Extension Showcase"
                analyticsMetadata={{
                  button_type: 'primary_cta',
                  section: 'extension_showcase',
                  conversion_intent: 'extension_download'
                }}
                className="group relative overflow-hidden w-full sm:w-auto"
              >
                Add to Chrome - FREE
              </Button>
              
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-3">
                Works with <span className='text-text-primary-light dark:text-text-primary-dark font-medium'>LinkedIn</span>, <span className='text-text-primary-light dark:text-text-primary-dark font-medium'>Naukri</span>. Pin this to Toolbar.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}